import {
  Archive as ArchiveBox,
  ArrowCounterClockwise,
  Check,
  Envelope,
  EnvelopeOpen,
  FadersHorizontal,
  Keyboard as KeyboardIcon,
  LockSimple,
  Paperclip,
  Star,
  Tray,
  Trash,
  X,
} from "@phosphor-icons/react";
import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import DOMPurify from "dompurify";
import dayjs from "dayjs";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import type {
  MessageDto,
  SystemFolder,
  ThreadDetail,
  ThreadListItem,
  ThreadListMeta,
} from "@asc-mail/contracts";
import { MaterialIcon } from "../components/MaterialIcon";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { useMailShell } from "../components/MailShell";
import { absoluteAssetUrl, api, apiWithMeta, eventUrl } from "../lib/api";
import { displayMailLabelName } from "../lib/labels";
import { ignoreUnavailableAction } from "../lib/unavailable";
import { avatarColor, initials, cn } from "../lib/utils";

const folderConfig: Record<
  string,
  {
    title: string;
    folder: SystemFolder;
    starred?: boolean;
    important?: boolean;
  }
> = {
  inbox: { title: "Inbox", folder: "INBOX" },
  starred: { title: "Starred", folder: "INBOX", starred: true },
  important: { title: "Important", folder: "INBOX", important: true },
  sent: { title: "Sent", folder: "SENT" },
  draft: { title: "Drafts", folder: "DRAFT" },
  archive: { title: "Archive", folder: "ARCHIVE" },
  spam: { title: "Spam", folder: "SPAM" },
  trash: { title: "Trash", folder: "TRASH" },
};

interface LabelDto {
  id: string;
  name: string;
  color: string;
}
interface DeleteRequest {
  placementIds: string[];
  closeDetail: boolean;
}

function ToolbarAction({
  label,
  disabled = false,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <span
      className="mail-toolbar-action"
      data-tooltip={disabled ? `${label} · Select an email first` : label}
    >
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={label}
        disabled={disabled}
        onClick={onClick}
      >
        {children}
      </Button>
    </span>
  );
}

function InputToolsButton() {
  return (
    <button
      type="button"
      className="mail-input-tools"
      aria-label="Input tools"
      onClick={ignoreUnavailableAction}
    >
      <KeyboardIcon size={18} aria-hidden="true" />
      <MaterialIcon>arrow_drop_down</MaterialIcon>
    </button>
  );
}

function MailListFooter() {
  return (
    <footer className="mail-list-footer" aria-label="Mailbox information">
      <div className="mail-storage-status">
        <div className="mail-storage-meter" aria-hidden="true">
          <span />
        </div>
        <button type="button" onClick={ignoreUnavailableAction}>
          <span>0% of 15 GB used</span>
          <MaterialIcon>open_in_new</MaterialIcon>
        </button>
      </div>
      <nav className="mail-footer-policy-links" aria-label="Legal links">
        <button type="button" onClick={ignoreUnavailableAction}>
          Terms
        </button>
        <span aria-hidden="true">·</span>
        <button type="button" onClick={ignoreUnavailableAction}>
          Privacy
        </button>
        <span aria-hidden="true">·</span>
        <button type="button" onClick={ignoreUnavailableAction}>
          Gmail Program Policies
        </button>
      </nav>
      <div className="mail-account-activity">
        <div>Last account activity: 0 minutes ago</div>
        <button type="button" onClick={ignoreUnavailableAction}>
          Details
        </button>
      </div>
    </footer>
  );
}

function LabelPicker({
  labels,
  activeIds,
  onToggle,
  label = "Labels",
}: {
  labels: LabelDto[];
  activeIds: Set<string>;
  onToggle: (labelId: string) => void;
  label?: string;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <span className="mail-toolbar-action" data-tooltip={label}>
          <Button variant="ghost" size="icon-sm" aria-label={label}>
            <MaterialIcon>label</MaterialIcon>
          </Button>
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64 p-2">
        <div className="px-2.5 pb-1.5 pt-1 text-xs font-[650] text-muted-foreground">
          Label as
        </div>
        {labels.length ? (
          labels.map((item) => (
            <DropdownMenuItem
              key={item.id}
              onSelect={(event) => {
                event.preventDefault();
                onToggle(item.id);
              }}
            >
              <MaterialIcon
                className={
                  activeIds.has(item.id)
                    ? "filled text-primary"
                    : "text-muted-foreground"
                }
              >
                {activeIds.has(item.id)
                  ? "check_box"
                  : "check_box_outline_blank"}
              </MaterialIcon>
              <span className="min-w-0 flex-1 truncate">
                {displayMailLabelName(item.name)}
              </span>
            </DropdownMenuItem>
          ))
        ) : (
          <div className="px-2.5 py-3 text-sm text-muted-foreground">
            No labels
          </div>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={ignoreUnavailableAction}>
          <MaterialIcon>add</MaterialIcon>Create new
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MoveMenu({
  folder,
  onMove,
}: {
  folder: SystemFolder;
  onMove: (action: string) => void;
}) {
  const entries = [
    ...(folder !== "INBOX"
      ? [{ label: "Inbox", icon: "inbox", action: "MOVE_INBOX" }]
      : []),
    ...(folder !== "ARCHIVE" && folder !== "DRAFT"
      ? [{ label: "Archive", icon: "archive", action: "ARCHIVE" }]
      : []),
    ...(folder !== "SPAM" && folder !== "DRAFT"
      ? [{ label: "Spam", icon: "report", action: "MOVE_SPAM" }]
      : []),
    ...(folder !== "TRASH"
      ? [{ label: "Trash", icon: "delete", action: "MOVE_TRASH" }]
      : []),
  ];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <span className="mail-toolbar-action" data-tooltip="Move to">
          <Button variant="ghost" size="icon-sm" aria-label="Move to">
            <MaterialIcon>drive_file_move</MaterialIcon>
          </Button>
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48 p-2">
        <div className="px-2.5 pb-1.5 pt-1 text-xs font-[650] text-muted-foreground">
          Move to
        </div>
        {entries.map((entry) => (
          <DropdownMenuItem
            key={entry.action}
            onSelect={() => onMove(entry.action)}
          >
            <MaterialIcon>{entry.icon}</MaterialIcon>
            {entry.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const actionFeedback: Record<string, string> = {
  ARCHIVE: "Conversation archived",
  MOVE_INBOX: "Moved to Inbox",
  MOVE_SPAM: "Conversation moved to Spam",
  MOVE_TRASH: "Conversation moved to Trash",
  RESTORE: "Conversation restored",
  MARK_READ: "Marked as read",
  MARK_UNREAD: "Marked as unread",
};

const undoActionFor: Partial<Record<string, string>> = {
  ARCHIVE: "MOVE_INBOX",
  MOVE_SPAM: "MOVE_INBOX",
  MOVE_TRASH: "RESTORE",
};

function actionMessage(actionName: string, count: number) {
  const base = actionFeedback[actionName];
  if (!base || count < 2) return base;
  if (actionName === "ARCHIVE") return `${count} conversations archived`;
  if (actionName === "MOVE_SPAM") return `${count} conversations moved to Spam`;
  if (actionName === "MOVE_TRASH")
    return `${count} conversations moved to Trash`;
  if (actionName === "RESTORE") return `${count} conversations restored`;
  return base;
}

function showGmailNotice(message: string, onUndo?: () => Promise<void> | void) {
  toast.custom(
    (toastId) => (
      <div className="gmail-action-toast" role="status">
        <span>{message}</span>
        {onUndo ? (
          <button
            type="button"
            className="gmail-action-toast-undo"
            onClick={() => {
              toast.dismiss(toastId);
              void Promise.resolve(onUndo()).catch((error) =>
                toast.error(
                  error instanceof Error
                    ? error.message
                    : "Unable to undo action",
                ),
              );
            }}
          >
            Undo
          </button>
        ) : null}
        <button
          type="button"
          className="gmail-action-toast-close"
          aria-label="Dismiss notification"
          onClick={() => toast.dismiss(toastId)}
        >
          <X size={18} />
        </button>
      </div>
    ),
    { duration: 8000, position: "bottom-left" },
  );
}

function PermanentDeleteDialog({
  request,
  pending,
  onCancel,
  onConfirm,
}: {
  request: DeleteRequest | null;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const count = request?.placementIds.length ?? 0;
  return (
    <Dialog.Root
      open={Boolean(request)}
      onOpenChange={(open) => {
        if (!open && !pending) onCancel();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[70] bg-[#111820]/45 backdrop-blur-[1px] data-[state=open]:animate-in data-[state=open]:fade-in" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-[71] w-[min(420px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-card p-6 text-card-foreground shadow-[0_28px_90px_rgba(12,22,38,.3)] outline-none">
          <Dialog.Title className="text-[18px] font-[670] tracking-[-.025em]">
            Delete permanently?
          </Dialog.Title>
          <Dialog.Description className="mt-2 text-sm leading-6 text-muted-foreground">
            {count > 1 ? `${count} selected conversations` : "This message"}{" "}
            will be permanently removed from this mailbox and cannot be
            recovered.
          </Dialog.Description>
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="outline" disabled={pending} onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={pending}
              onClick={onConfirm}
            >
              {pending ? "Deleting" : "Delete permanently"}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function sanitizeMessageHtml(value: string) {
  const template = document.createElement("template");
  template.innerHTML = DOMPurify.sanitize(value);
  return template.innerHTML;
}

async function hydrateProtectedImages(value: string, signal: AbortSignal) {
  const template = document.createElement("template");
  template.innerHTML = sanitizeMessageHtml(value);
  const objectUrls: string[] = [];
  await Promise.all(
    Array.from(
      template.content.querySelectorAll<HTMLImageElement>("img[data-asset-id]"),
    ).map(async (image) => {
      const assetId = image.dataset.assetId;
      if (!assetId || !/^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(assetId)) return;
      try {
        const response = await fetch(
          absoluteAssetUrl(`/api/v1/mail/assets/${assetId}/content`),
          { credentials: "include", signal },
        );
        if (!response.ok)
          throw new Error(`Image failed to load (${response.status})`);
        const objectUrl = URL.createObjectURL(await response.blob());
        objectUrls.push(objectUrl);
        image.src = objectUrl;
      } catch {
        if (!signal.aborted) image.removeAttribute("src");
      }
    }),
  );
  return { html: template.innerHTML, objectUrls };
}

function messageDocument(html: string, darkMode: boolean) {
  const palette = darkMode
    ? {
        background: "#151b24",
        foreground: "#e8edf4",
        link: "#9bb1ff",
        quote: "#a6b0bd",
        quoteBorder: "#46566a",
      }
    : {
        background: "#ffffff",
        foreground: "#1c232d",
        link: "#315dcc",
        quote: "#5a6674",
        quoteBorder: "#bdc9dc",
      };
  return `<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>html{color-scheme:${darkMode ? "dark" : "light"}}html,body{margin:0;padding:0;overflow:hidden;background:${palette.background}}body{color:${palette.foreground};font:13px/1.5 Roboto,Arial,sans-serif;overflow-wrap:anywhere}p{margin:0 0 10px}a{color:${palette.link}}img{display:block;max-width:100%;height:auto;margin:14px 0;cursor:zoom-in}img:focus-visible{outline:3px solid ${palette.link};outline-offset:3px}table{max-width:100%;border-collapse:collapse}blockquote{border-left:3px solid ${palette.quoteBorder};margin:12px 0;padding-left:12px;color:${palette.quote}}</style></head><body>${html}</body></html>`;
}

function MessageBodyFrame({
  messageId,
  bodyHtml,
  darkMode,
}: {
  messageId: string;
  bodyHtml: string;
  darkMode: boolean;
}) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const interactionCleanupRef = useRef<(() => void) | null>(null);
  const [srcDoc, setSrcDoc] = useState<string | null>(null);
  const [height, setHeight] = useState(40);
  const [previewImage, setPreviewImage] = useState<{
    src: string;
    alt: string;
  } | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let objectUrls: string[] = [];
    setSrcDoc(null);
    setHeight(40);
    setPreviewImage(null);
    void hydrateProtectedImages(bodyHtml, controller.signal).then((result) => {
      if (controller.signal.aborted) {
        result.objectUrls.forEach((url) => URL.revokeObjectURL(url));
        return;
      }
      objectUrls = result.objectUrls;
      setSrcDoc(messageDocument(result.html, darkMode));
    });
    return () => {
      controller.abort();
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [bodyHtml, darkMode]);

  const measure = useCallback(() => {
    const body = frameRef.current?.contentDocument?.body;
    if (!body) return;
    const nextHeight = Math.max(40, Math.ceil(body.scrollHeight));
    setHeight((current) =>
      Math.abs(current - nextHeight) > 1 ? nextHeight : current,
    );
  }, []);

  const handleLoad = useCallback(() => {
    resizeObserverRef.current?.disconnect();
    interactionCleanupRef.current?.();
    interactionCleanupRef.current = null;
    const body = frameRef.current?.contentDocument?.body;
    if (!body) return;
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(body);
    resizeObserverRef.current = observer;
    body.querySelectorAll<HTMLImageElement>("img").forEach((image) => {
      image.tabIndex = 0;
      image.setAttribute("role", "button");
      image.setAttribute(
        "aria-label",
        image.alt ? `Preview image: ${image.alt}` : "Preview image",
      );
      image.addEventListener("load", measure, { once: true });
      image.addEventListener("error", measure, { once: true });
    });

    const openPreview = (image: HTMLImageElement) => {
      const src = image.currentSrc || image.src;
      if (!src) return;
      setPreviewImage({ src, alt: image.alt.trim() || "Email image" });
    };
    const handleImageClick = (event: MouseEvent) => {
      const image = (event.target as Element | null)?.closest?.(
        "img",
      ) as HTMLImageElement | null;
      if (!image) return;
      event.preventDefault();
      event.stopPropagation();
      openPreview(image);
    };
    const handleImageKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      const image = (event.target as Element | null)?.closest?.(
        "img",
      ) as HTMLImageElement | null;
      if (!image) return;
      event.preventDefault();
      event.stopPropagation();
      openPreview(image);
    };
    body.addEventListener("click", handleImageClick, true);
    body.addEventListener("keydown", handleImageKeyDown, true);
    interactionCleanupRef.current = () => {
      body.removeEventListener("click", handleImageClick, true);
      body.removeEventListener("keydown", handleImageKeyDown, true);
    };
  }, [measure]);

  useEffect(
    () => () => {
      resizeObserverRef.current?.disconnect();
      interactionCleanupRef.current?.();
    },
    [],
  );

  if (!srcDoc)
    return (
      <div
        className="message-frame-loading"
        aria-label="Loading message body"
      />
    );
  return (
    <>
      <iframe
        ref={frameRef}
        title={`Message body ${messageId}`}
        sandbox="allow-same-origin"
        scrolling="no"
        className="message-iframe"
        style={{ height }}
        onLoad={handleLoad}
        srcDoc={srcDoc}
      />
      <Dialog.Root
        open={Boolean(previewImage)}
        onOpenChange={(open) => {
          if (!open) setPreviewImage(null);
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="message-image-preview-overlay" />
          <Dialog.Content className="message-image-preview-dialog">
            <Dialog.Title className="sr-only">Image preview</Dialog.Title>
            <Dialog.Description className="sr-only">
              Full-size preview of the selected email image.
            </Dialog.Description>
            {previewImage ? (
              <img
                src={previewImage.src}
                alt={previewImage.alt}
                className="message-image-preview-content"
              />
            ) : null}
            <Dialog.Close asChild>
              <button
                type="button"
                className="message-image-preview-close"
                aria-label="Close image preview"
              >
                <X size={22} weight="bold" />
              </button>
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}

export function MailboxPage() {
  const { session, mailbox, compose } = useMailShell();
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [cursorStack, setCursorStack] = useState<Array<string | null>>([null]);
  const [deleteRequest, setDeleteRequest] = useState<DeleteRequest | null>(
    null,
  );
  const suppressDetailRefreshRef = useRef(false);
  const [systemDark, setSystemDark] = useState(
    () => window.matchMedia("(prefers-color-scheme: dark)").matches,
  );
  const darkMode =
    session.theme === "DARK" || (session.theme === "SYSTEM" && systemDark);
  const folderKey = params.folder ?? "inbox";
  const config = folderConfig[folderKey] ?? folderConfig.inbox!;
  const q = searchParams.get("q") ?? "";
  const labelId = searchParams.get("labelId") ?? "";
  const unreadOnly = searchParams.get("unread") === "true";
  const starredOnly = searchParams.get("starred") === "true";
  const attachmentOnly = searchParams.get("hasAttachment") === "true";
  const activeFilterCount =
    Number(unreadOnly) +
    Number(starredOnly && !config.starred) +
    Number(attachmentOnly);
  const filterSignature = `${mailbox?.id ?? ""}:${folderKey}:${q}:${labelId}:${unreadOnly}:${starredOnly}:${attachmentOnly}`;
  useEffect(() => {
    setPageIndex(0);
    setCursorStack([null]);
    setSelectedIds([]);
  }, [filterSignature]);
  useEffect(() => {
    if (!params.threadId) suppressDetailRefreshRef.current = false;
  }, [params.threadId]);
  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const syncTheme = (event: MediaQueryListEvent) =>
      setSystemDark(event.matches);
    media.addEventListener("change", syncTheme);
    return () => media.removeEventListener("change", syncTheme);
  }, []);
  const currentCursor = cursorStack[pageIndex] ?? null;
  const queryString = useMemo(
    () =>
      new URLSearchParams({
        limit: "50",
        folder: config.folder,
        ...(currentCursor ? { cursor: currentCursor } : {}),
        ...(q ? { q } : {}),
        ...(labelId ? { labelId } : {}),
        ...(config.starred || starredOnly ? { starred: "true" } : {}),
        ...(config.important ? { important: "true" } : {}),
        ...(unreadOnly ? { unread: "true" } : {}),
        ...(attachmentOnly ? { hasAttachment: "true" } : {}),
      }).toString(),
    [
      attachmentOnly,
      config,
      currentCursor,
      labelId,
      q,
      starredOnly,
      unreadOnly,
    ],
  );
  const listKey = [
    "mail",
    mailbox?.id,
    "threads",
    folderKey,
    q,
    labelId,
    unreadOnly,
    starredOnly,
    attachmentOnly,
    currentCursor,
  ];
  const list = useQuery({
    queryKey: listKey,
    queryFn: () =>
      apiWithMeta<ThreadListItem[], ThreadListMeta>(
        `/mail/mailboxes/${mailbox!.id}/threads?${queryString}`,
      ),
    enabled: Boolean(mailbox),
  });
  const detail = useQuery({
    queryKey: ["mail", mailbox?.id, "thread", params.threadId, folderKey],
    queryFn: () =>
      api<ThreadDetail>(
        `/mail/mailboxes/${mailbox!.id}/threads/${params.threadId}?folder=${config.folder}`,
      ),
    enabled: Boolean(mailbox && params.threadId),
  });
  const labels = useQuery({
    queryKey: ["mail", mailbox?.id, "labels"],
    queryFn: () => api<LabelDto[]>(`/mail/mailboxes/${mailbox!.id}/labels`),
    enabled: Boolean(mailbox),
  });
  const availableLabels = useMemo(() => {
    const byName = new Map<string, LabelDto>();
    for (const label of labels.data ?? []) {
      const key = displayMailLabelName(label.name).toLocaleLowerCase();
      if (!byName.has(key)) byName.set(key, label);
    }
    return [...byName.values()];
  }, [labels.data]);

  useEffect(() => {
    if (!mailbox) return;
    const events = new EventSource(eventUrl(mailbox.id), {
      withCredentials: true,
    });
    const invalidate = () => {
      if (suppressDetailRefreshRef.current) {
        void queryClient.invalidateQueries({
          queryKey: ["mail", mailbox.id, "threads"],
        });
        void queryClient.invalidateQueries({
          queryKey: ["mail", mailbox.id, "counts"],
        });
        void queryClient.invalidateQueries({ queryKey: ["mail", "mailboxes"] });
        return;
      }
      void queryClient.invalidateQueries({ queryKey: ["mail", mailbox.id] });
      void queryClient.invalidateQueries({ queryKey: ["mail", "mailboxes"] });
    };
    events.addEventListener("mailbox.changed", invalidate);
    return () => {
      events.removeEventListener("mailbox.changed", invalidate);
      events.close();
    };
  }, [mailbox, queryClient]);

  const action = useMutation({
    mutationFn: ({
      placementId,
      action: actionName,
    }: {
      placementId: string;
      action: string;
    }) =>
      api(`/mail/mailboxes/${mailbox!.id}/placements/${placementId}`, {
        method: "PATCH",
        body: JSON.stringify({ action: actionName }),
      }),
    onMutate: async ({ placementId, action: actionName }) => {
      await queryClient.cancelQueries({
        queryKey: ["mail", mailbox?.id, "threads"],
      });
      const previous = queryClient.getQueriesData({
        queryKey: ["mail", mailbox?.id, "threads"],
      });
      queryClient.setQueriesData<{ data: ThreadListItem[]; meta: unknown }>(
        { queryKey: ["mail", mailbox?.id, "threads"] },
        (old) => {
          if (!old) return old;
          const removed = [
            "ARCHIVE",
            "MOVE_INBOX",
            "MOVE_TRASH",
            "MOVE_SPAM",
            "RESTORE",
            "DELETE_FOREVER",
          ].includes(actionName);
          return {
            ...old,
            data: old.data
              .filter((item) => !(removed && item.placementId === placementId))
              .map((item) =>
                item.placementId !== placementId
                  ? item
                  : {
                      ...item,
                      starred:
                        actionName === "STAR"
                          ? true
                          : actionName === "UNSTAR"
                            ? false
                            : item.starred,
                      unreadCount:
                        actionName === "MARK_READ"
                          ? 0
                          : actionName === "MARK_UNREAD"
                            ? Math.max(1, item.unreadCount)
                            : item.unreadCount,
                    },
              ),
          };
        },
      );
      return { previous };
    },
    onError: (error, _variables, context) => {
      for (const [key, value] of context?.previous ?? [])
        queryClient.setQueryData(key, value);
      toast.error(error instanceof Error ? error.message : "Action failed");
    },
    onSettled: (_data, _error, variables) => {
      if (variables.action === "MARK_UNREAD") {
        void queryClient.invalidateQueries({
          queryKey: ["mail", mailbox?.id, "threads"],
        });
        void queryClient.invalidateQueries({
          queryKey: ["mail", mailbox?.id, "counts"],
        });
        void queryClient.invalidateQueries({ queryKey: ["mail", "mailboxes"] });
        return;
      }
      void queryClient.invalidateQueries({ queryKey: ["mail", mailbox?.id] });
      void queryClient.invalidateQueries({ queryKey: ["mail", "mailboxes"] });
    },
  });
  const assignLabels = useMutation({
    mutationFn: ({
      placementId,
      labelIds,
    }: {
      placementId: string;
      labelIds: string[];
    }) =>
      api(`/mail/mailboxes/${mailbox!.id}/placements/${placementId}/labels`, {
        method: "PUT",
        body: JSON.stringify({ labelIds }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["mail", mailbox?.id] });
    },
    onError: (error) =>
      toast.error(
        error instanceof Error ? error.message : "Unable to update labels",
      ),
  });

  const actSelected = async (actionName: string) => {
    const placementIds = [...selectedIds];
    try {
      await Promise.all(
        placementIds.map((placementId) =>
          action.mutateAsync({ placementId, action: actionName }),
        ),
      );
      setSelectedIds([]);
      const message = actionMessage(actionName, placementIds.length);
      const undoAction = undoActionFor[actionName];
      if (message)
        showGmailNotice(
          message,
          undoAction
            ? async () => {
                await Promise.all(
                  placementIds.map((placementId) =>
                    action.mutateAsync({ placementId, action: undoAction }),
                  ),
                );
                showGmailNotice(
                  placementIds.length > 1 ? "Actions undone" : "Action undone",
                );
              }
            : undefined,
        );
    } catch {
      // Mutation feedback and rollback are handled centrally.
    }
  };
  const closeThread = () => {
    if (mailbox)
      void navigate(
        `/${mailbox.id}/${folderKey}${searchParams.toString() ? `?${searchParams}` : ""}`,
      );
  };
  const selectThread = (threadId: string) => {
    if (mailbox)
      void navigate(
        `/${mailbox.id}/${folderKey}/thread/${threadId}${searchParams.toString() ? `?${searchParams}` : ""}`,
      );
  };
  const toggleFilter = (key: "unread" | "starred" | "hasAttachment") => {
    const next = new URLSearchParams(searchParams);
    if (next.get(key) === "true") next.delete(key);
    else next.set(key, "true");
    setSelectedIds([]);
    setSearchParams(next, { replace: true });
  };
  const clearFilters = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("unread");
    next.delete("starred");
    next.delete("hasAttachment");
    setSelectedIds([]);
    setSearchParams(next, { replace: true });
  };
  const runDetailAction = (actionName: string) => {
    if (!detail.data) return;
    const closesDetail = [
      "ARCHIVE",
      "MOVE_INBOX",
      "MOVE_TRASH",
      "MOVE_SPAM",
      "RESTORE",
      "MARK_UNREAD",
    ].includes(actionName);
    if (actionName === "MARK_UNREAD") suppressDetailRefreshRef.current = true;
    action.mutate(
      { placementId: detail.data.placement.id, action: actionName },
      {
        onSuccess: () => {
          const message = actionMessage(actionName, 1);
          const undoAction = undoActionFor[actionName];
          if (message)
            showGmailNotice(
              message,
              undoAction
                ? async () => {
                    await action.mutateAsync({
                      placementId: detail.data!.placement.id,
                      action: undoAction,
                    });
                    showGmailNotice("Action undone");
                  }
                : undefined,
            );
          if (closesDetail) closeThread();
        },
        onError: () => {
          suppressDetailRefreshRef.current = false;
        },
      },
    );
  };
  const confirmPermanentDelete = async () => {
    if (!deleteRequest) return;
    try {
      await Promise.all(
        deleteRequest.placementIds.map((placementId) =>
          action.mutateAsync({ placementId, action: "DELETE_FOREVER" }),
        ),
      );
      setSelectedIds([]);
      if (deleteRequest.closeDetail) closeThread();
      setDeleteRequest(null);
      showGmailNotice("Conversation permanently deleted");
    } catch {
      // Mutation feedback and rollback are handled centrally.
    }
  };

  if (!mailbox)
    return (
      <div className="grid h-full place-items-center text-sm text-muted-foreground">
        No mailboxes available
      </div>
    );
  const rows = list.data?.data ?? [];
  const inTrash = config.folder === "TRASH";
  const inDrafts = config.folder === "DRAFT";
  const moveBackToInbox =
    config.folder === "ARCHIVE" || config.folder === "SPAM";
  const selectedRows = rows.filter((row) =>
    selectedIds.includes(row.placementId),
  );
  const selectedActiveLabelIds = new Set(
    availableLabels
      .filter(
        (label) =>
          selectedRows.length > 0 &&
          selectedRows.every((row) =>
            row.labels.some((item) => item.id === label.id),
          ),
      )
      .map((label) => label.id),
  );
  const selectedContainUnread = selectedRows.some((row) => row.unreadCount > 0);
  const readAction =
    selectedRows.length > 0 && !selectedContainUnread
      ? "MARK_UNREAD"
      : "MARK_READ";
  const readLabel =
    readAction === "MARK_UNREAD" ? "Mark as unread" : "Mark as read";
  const total = list.data?.meta.total ?? rows.length;
  const firstItem = total && rows.length ? pageIndex * 50 + 1 : 0;
  const lastItem = Math.min(pageIndex * 50 + rows.length, total);
  const detailThreadIndex = params.threadId
    ? rows.findIndex((row) => row.id === params.threadId)
    : -1;
  const detailPosition =
    detailThreadIndex >= 0 ? pageIndex * 50 + detailThreadIndex + 1 : null;
  const previousThread =
    detailThreadIndex > 0 ? rows[detailThreadIndex - 1] : undefined;
  const nextThread =
    detailThreadIndex >= 0 && detailThreadIndex < rows.length - 1
      ? rows[detailThreadIndex + 1]
      : undefined;
  const toggleSelectedLabel = async (labelId: string) => {
    const adding = !selectedActiveLabelIds.has(labelId);
    try {
      await Promise.all(
        selectedRows.map((row) => {
          const next = new Set(row.labels.map((label) => label.id));
          if (adding) next.add(labelId);
          else next.delete(labelId);
          return assignLabels.mutateAsync({
            placementId: row.placementId,
            labelIds: [...next],
          });
        }),
      );
      toast.success(adding ? "Label added" : "Label removed");
    } catch {
      // Mutation feedback is handled centrally.
    }
  };
  const nextPage = () => {
    const nextCursor = list.data?.meta.nextCursor;
    if (!nextCursor) return;
    setCursorStack((items) => {
      const next = items.slice(0, pageIndex + 1);
      next[pageIndex + 1] = nextCursor;
      return next;
    });
    setPageIndex((value) => value + 1);
    setSelectedIds([]);
  };
  const showCategories = folderKey === "inbox" && !labelId && !q;
  const runRowAction = (thread: ThreadListItem, actionName: string) => {
    action.mutate(
      { placementId: thread.placementId, action: actionName },
      {
        onSuccess: () => {
          const message = actionMessage(actionName, 1);
          const undoAction = undoActionFor[actionName];
          if (message)
            showGmailNotice(
              message,
              undoAction
                ? async () => {
                    await action.mutateAsync({
                      placementId: thread.placementId,
                      action: undoAction,
                    });
                    showGmailNotice("Action undone");
                  }
                : undefined,
            );
        },
      },
    );
  };
  return (
    <>
      <div className="thread-layout no-pane">
        {!params.threadId ? (
          <div className="thread-list h-full overflow-y-auto">
            <div
              className={cn(
                "mail-list-header mail-list-toolbar sticky top-0 z-20 flex items-center gap-1 border-b px-3",
                showCategories && "has-category-tabs border-b-0",
              )}
            >
              <div className="mail-select-control">
                <label className="grid size-8 place-items-center">
                  <input
                    aria-label="Select all"
                    type="checkbox"
                    checked={
                      rows.length > 0 && selectedIds.length === rows.length
                    }
                    onChange={(event) =>
                      setSelectedIds(
                        event.target.checked
                          ? rows.map((row) => row.placementId)
                          : [],
                      )
                    }
                  />
                </label>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="mail-select-menu"
                      aria-label="Select messages"
                    >
                      <MaterialIcon>arrow_drop_down</MaterialIcon>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-44">
                    <DropdownMenuItem
                      onSelect={() =>
                        setSelectedIds(rows.map((row) => row.placementId))
                      }
                    >
                      All
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => setSelectedIds([])}>
                      None
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() =>
                        setSelectedIds(
                          rows
                            .filter((row) => row.unreadCount === 0)
                            .map((row) => row.placementId),
                        )
                      }
                    >
                      Read
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() =>
                        setSelectedIds(
                          rows
                            .filter((row) => row.unreadCount > 0)
                            .map((row) => row.placementId),
                        )
                      }
                    >
                      Unread
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() =>
                        setSelectedIds(
                          rows
                            .filter((row) => row.starred)
                            .map((row) => row.placementId),
                        )
                      }
                    >
                      Starred
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() =>
                        setSelectedIds(
                          rows
                            .filter((row) => !row.starred)
                            .map((row) => row.placementId),
                        )
                      }
                    >
                      Unstarred
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              {selectedIds.length ? (
                <>
                  {!inDrafts ? (
                    inTrash ? (
                      <ToolbarAction
                        label="Restore"
                        onClick={() => void actSelected("RESTORE")}
                      >
                        <ArrowCounterClockwise size={18} />
                      </ToolbarAction>
                    ) : moveBackToInbox ? (
                      <ToolbarAction
                        label="Move to Inbox"
                        onClick={() => void actSelected("MOVE_INBOX")}
                      >
                        <Tray size={18} />
                      </ToolbarAction>
                    ) : (
                      <ToolbarAction
                        label="Archive"
                        onClick={() => void actSelected("ARCHIVE")}
                      >
                        <ArchiveBox size={18} />
                      </ToolbarAction>
                    )
                  ) : null}
                  {!inDrafts && !inTrash ? (
                    <ToolbarAction
                      label="Report spam"
                      onClick={() => void actSelected("MOVE_SPAM")}
                    >
                      <MaterialIcon>report</MaterialIcon>
                    </ToolbarAction>
                  ) : null}
                  <ToolbarAction
                    label={inTrash ? "Delete permanently" : "Delete"}
                    onClick={() =>
                      inTrash
                        ? setDeleteRequest({
                            placementIds: selectedIds,
                            closeDetail: false,
                          })
                        : void actSelected("MOVE_TRASH")
                    }
                  >
                    <Trash size={18} />
                  </ToolbarAction>
                  <div className="mx-1 h-5 w-px bg-border" />
                  {!inDrafts ? (
                    <ToolbarAction
                      label={readLabel}
                      onClick={() => void actSelected(readAction)}
                    >
                      {readAction === "MARK_UNREAD" ? (
                        <Envelope size={18} />
                      ) : (
                        <EnvelopeOpen size={18} />
                      )}
                    </ToolbarAction>
                  ) : null}
                  <ToolbarAction
                    label="Snooze"
                    onClick={ignoreUnavailableAction}
                  >
                    <MaterialIcon>schedule</MaterialIcon>
                  </ToolbarAction>
                  <ToolbarAction
                    label="Add to Tasks"
                    onClick={ignoreUnavailableAction}
                  >
                    <MaterialIcon>add_task</MaterialIcon>
                  </ToolbarAction>
                  <div className="mx-1 h-5 w-px bg-border" />
                  <MoveMenu
                    folder={config.folder}
                    onMove={(actionName) => void actSelected(actionName)}
                  />
                  <LabelPicker
                    labels={availableLabels}
                    activeIds={selectedActiveLabelIds}
                    onToggle={(id) => void toggleSelectedLabel(id)}
                  />
                  <ToolbarAction label="More" onClick={ignoreUnavailableAction}>
                    <MaterialIcon>more_vert</MaterialIcon>
                  </ToolbarAction>
                  <span className="ml-1 text-xs text-muted-foreground">
                    {selectedIds.length} selected
                  </span>
                </>
              ) : (
                <>
                  <ToolbarAction
                    label="Refresh"
                    onClick={() => void list.refetch()}
                  >
                    <MaterialIcon>refresh</MaterialIcon>
                  </ToolbarAction>
                  <ToolbarAction label="More" onClick={ignoreUnavailableAction}>
                    <MaterialIcon>more_vert</MaterialIcon>
                  </ToolbarAction>
                </>
              )}
              {!selectedIds.length ? (
                <>
                  {q ? (
                    <span className="ml-1 truncate text-xs text-muted-foreground">
                      Search: {q}
                    </span>
                  ) : null}
                  {activeFilterCount ? (
                    <span className="rounded-md bg-primary/10 px-2 py-0.5 text-[11px] font-[650] text-primary">
                      Filters {activeFilterCount}
                    </span>
                  ) : null}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        className="relative"
                        variant={activeFilterCount ? "secondary" : "ghost"}
                        size="icon-sm"
                        aria-label="Filter messages"
                        title="Filter messages"
                      >
                        <FadersHorizontal size={18} />
                        {activeFilterCount ? (
                          <span className="absolute right-0.5 top-0.5 size-1.5 rounded-full bg-primary" />
                        ) : null}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52 p-2">
                      <div className="px-2.5 pb-1.5 pt-1 text-xs font-[650] text-muted-foreground">
                        Filter messages
                      </div>
                      {!inDrafts ? (
                        <DropdownMenuItem
                          onSelect={() => toggleFilter("unread")}
                        >
                          <span className="grid size-4 place-items-center">
                            {unreadOnly ? (
                              <Check size={14} weight="bold" />
                            ) : null}
                          </span>
                          Unread
                        </DropdownMenuItem>
                      ) : null}
                      {!config.starred ? (
                        <DropdownMenuItem
                          onSelect={() => toggleFilter("starred")}
                        >
                          <span className="grid size-4 place-items-center">
                            {starredOnly ? (
                              <Check size={14} weight="bold" />
                            ) : null}
                          </span>
                          Starred
                        </DropdownMenuItem>
                      ) : null}
                      <DropdownMenuItem
                        onSelect={() => toggleFilter("hasAttachment")}
                      >
                        <span className="grid size-4 place-items-center">
                          {attachmentOnly ? (
                            <Check size={14} weight="bold" />
                          ) : null}
                        </span>
                        Has attachment
                      </DropdownMenuItem>
                      {activeFilterCount ? (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-muted-foreground"
                            onSelect={clearFilters}
                          >
                            Clear filters
                          </DropdownMenuItem>
                        </>
                      ) : null}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : null}
              <div className="mail-pagination">
                <span aria-live="polite">
                  {list.isPending
                    ? "\u00a0"
                    : `${firstItem}-${lastItem} of ${total}`}
                </span>
                <button
                  type="button"
                  aria-label="Previous page"
                  disabled={pageIndex === 0}
                  onClick={() => {
                    setPageIndex((value) => Math.max(0, value - 1));
                    setSelectedIds([]);
                  }}
                >
                  <MaterialIcon>chevron_left</MaterialIcon>
                </button>
                <button
                  type="button"
                  aria-label="Next page"
                  disabled={!list.data?.meta.nextCursor}
                  onClick={nextPage}
                >
                  <MaterialIcon>chevron_right</MaterialIcon>
                </button>
                <InputToolsButton />
              </div>
            </div>
            {showCategories ? (
              <div
                className="mail-category-tabs"
                role="tablist"
                aria-label="Inbox categories"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected="true"
                  className="active"
                >
                  <MaterialIcon className="filled">inbox</MaterialIcon>
                  <span>Primary</span>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected="false"
                  onClick={ignoreUnavailableAction}
                >
                  <MaterialIcon>sell</MaterialIcon>
                  <span>Promotions</span>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected="false"
                  onClick={ignoreUnavailableAction}
                >
                  <MaterialIcon>group</MaterialIcon>
                  <span>Social</span>
                </button>
              </div>
            ) : null}
            {list.isPending ? (
              <ThreadSkeleton categories={showCategories} />
            ) : list.isError ? (
              <EmptyState
                title="Unable to load messages"
                body="Refresh and try again."
              />
            ) : rows.length === 0 ? (
              <EmptyState
                title="No messages"
                body={
                  q || activeFilterCount
                    ? "No messages match your filters."
                    : "This folder is empty."
                }
              />
            ) : (
              rows.map((thread) => (
                <ThreadRow
                  key={thread.id}
                  thread={thread}
                  folder={config.folder}
                  selected={params.threadId === thread.id}
                  checked={selectedIds.includes(thread.placementId)}
                  onCheck={(checked) =>
                    setSelectedIds((items) =>
                      checked
                        ? [...items, thread.placementId]
                        : items.filter((id) => id !== thread.placementId),
                    )
                  }
                  onOpen={() => selectThread(thread.id)}
                  onStar={() =>
                    action.mutate({
                      placementId: thread.placementId,
                      action: thread.starred ? "UNSTAR" : "STAR",
                    })
                  }
                  onAction={(actionName) => runRowAction(thread, actionName)}
                />
              ))
            )}
            {!list.isPending && !list.isError ? <MailListFooter /> : null}
          </div>
        ) : null}
        {params.threadId ? (
          <div className="thread-detail-pane h-full overflow-y-auto">
            <ThreadView
              detail={detail.data}
              loading={detail.isPending}
              onBack={closeThread}
              folder={config.folder}
              onAction={runDetailAction}
              onPermanentDelete={() => {
                if (detail.data)
                  setDeleteRequest({
                    placementIds: [detail.data.placement.id],
                    closeDetail: true,
                  });
              }}
              labels={availableLabels}
              onLabels={(labelIds) => {
                if (detail.data)
                  assignLabels.mutate(
                    { placementId: detail.data.placement.id, labelIds },
                    { onSuccess: () => toast.success("Labels updated") },
                  );
              }}
              compose={compose}
              mailboxAddress={mailbox.address}
              darkMode={darkMode}
              conversationPosition={detailPosition}
              conversationTotal={total}
              onPreviousConversation={
                previousThread
                  ? () => selectThread(previousThread.id)
                  : undefined
              }
              onNextConversation={
                nextThread ? () => selectThread(nextThread.id) : undefined
              }
            />
          </div>
        ) : null}
      </div>
      <PermanentDeleteDialog
        request={deleteRequest}
        pending={action.isPending}
        onCancel={() => setDeleteRequest(null)}
        onConfirm={() => void confirmPermanentDelete()}
      />
    </>
  );
}

function ThreadRow({
  thread,
  folder,
  selected,
  checked,
  onCheck,
  onOpen,
  onStar,
  onAction,
}: {
  thread: ThreadListItem;
  folder: SystemFolder;
  selected: boolean;
  checked: boolean;
  onCheck: (value: boolean) => void;
  onOpen: () => void;
  onStar: () => void;
  onAction: (action: string) => void;
}) {
  const sender = thread.participants[0];
  const primaryAction =
    folder === "TRASH"
      ? { label: "Restore", icon: "restore_from_trash", action: "RESTORE" }
      : folder === "ARCHIVE" || folder === "SPAM"
        ? {
            label: "Move to Inbox",
            icon: "move_to_inbox",
            action: "MOVE_INBOX",
          }
        : folder === "DRAFT"
          ? null
          : { label: "Archive", icon: "archive", action: "ARCHIVE" };
  return (
    <div
      role="button"
      tabIndex={0}
      className={cn(
        "thread-row",
        selected && "selected",
        thread.unreadCount > 0 && "unread",
      )}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter") onOpen();
      }}
    >
      <label
        className="thread-checkbox grid size-8 place-items-center"
        onClick={(event) => event.stopPropagation()}
      >
        <input
          aria-label="Select message"
          type="checkbox"
          checked={checked}
          onChange={(event) => onCheck(event.target.checked)}
        />
      </label>
      <button
        className="thread-star grid size-8 place-items-center rounded-sm text-muted-foreground hover:bg-muted hover:text-[#f4b401]"
        aria-label={thread.starred ? "Remove star" : "Add star"}
        onClick={(event) => {
          event.stopPropagation();
          onStar();
        }}
      >
        <Star
          size={18}
          weight={thread.starred ? "fill" : "regular"}
          className={thread.starred ? "text-[#f4b401]" : ""}
        />
      </button>
      <div
        className="thread-sender text-sm"
        title={sender?.name || sender?.address || "Unknown sender"}
      >
        {sender?.name || sender?.address || "Unknown sender"}
        {thread.messageCount > 1 ? (
          <span className="ml-1 text-xs font-normal text-muted-foreground">
            {thread.messageCount}
          </span>
        ) : null}
      </div>
      <div
        className="thread-content min-w-0 text-sm"
        title={`${thread.subject}${thread.preview ? ` · ${thread.preview}` : ""}`}
      >
        {thread.labels.map((label) => (
          <span
            key={label.id}
            className="thread-label-chip"
            style={{ background: label.color }}
          >
            {displayMailLabelName(label.name)}
          </span>
        ))}
        <span className="thread-subject">{thread.subject}</span>
        {thread.preview ? (
          <span className="thread-preview"> · {thread.preview}</span>
        ) : null}
        {thread.hasAttachment ? (
          <Paperclip className="ml-1 inline" size={14} />
        ) : null}
      </div>
      <time
        className="thread-time text-right text-[11px]"
        dateTime={thread.lastMessageAt}
      >
        {formatThreadDate(thread.lastMessageAt)}
      </time>
      <div
        className="thread-row-actions"
        onClick={(event) => event.stopPropagation()}
      >
        {primaryAction ? (
          <button
            type="button"
            aria-label={primaryAction.label}
            title={primaryAction.label}
            onClick={() => onAction(primaryAction.action)}
          >
            <MaterialIcon>{primaryAction.icon}</MaterialIcon>
          </button>
        ) : null}
        {folder !== "TRASH" ? (
          <button
            type="button"
            aria-label="Delete"
            title="Delete"
            onClick={() => onAction("MOVE_TRASH")}
          >
            <MaterialIcon>delete</MaterialIcon>
          </button>
        ) : null}
        {folder !== "DRAFT" ? (
          <button
            type="button"
            aria-label={thread.unreadCount ? "Mark as read" : "Mark as unread"}
            title={thread.unreadCount ? "Mark as read" : "Mark as unread"}
            onClick={() =>
              onAction(thread.unreadCount ? "MARK_READ" : "MARK_UNREAD")
            }
          >
            <MaterialIcon>
              {thread.unreadCount ? "drafts" : "mail"}
            </MaterialIcon>
          </button>
        ) : null}
        <button
          type="button"
          aria-label="Snooze"
          title="Snooze"
          onClick={ignoreUnavailableAction}
        >
          <MaterialIcon>schedule</MaterialIcon>
        </button>
      </div>
    </div>
  );
}

function HeaderDetailRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <>
      <dt className="text-right text-[12px] leading-5 text-muted-foreground">
        {label}
      </dt>
      <dd className="min-w-0 break-words text-[12px] leading-5 text-foreground">
        {children}
      </dd>
    </>
  );
}

function formatParty(name: string, address: string) {
  return name ? `${name} <${address}>` : address;
}

function MessageHeaderDetails({ message }: { message: MessageDto }) {
  const recipients = (type: "TO" | "CC" | "BCC") =>
    message.recipients
      .filter((item) => item.type === type)
      .map((item) => formatParty(item.displayName, item.address))
      .join(", ");
  const to = recipients("TO");
  const cc = recipients("CC");
  const bcc = recipients("BCC");
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="View message details"
          title="View message details"
          className="mail-header-details-trigger grid size-5 shrink-0 place-items-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <MaterialIcon>arrow_drop_down</MaterialIcon>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={5}
        collisionPadding={12}
        className="mail-header-details max-h-[min(520px,calc(100vh-32px))] w-[min(440px,calc(100vw-32px))] overflow-y-auto p-4"
      >
        <dl className="grid grid-cols-[64px_minmax(0,1fr)] gap-x-3 gap-y-1">
          <HeaderDetailRow label="From">
            {formatParty(message.fromName, message.fromAddress)}
          </HeaderDetailRow>
          {message.replyTo ? (
            <HeaderDetailRow label="Reply-To">
              {message.replyTo}
            </HeaderDetailRow>
          ) : null}
          {to ? <HeaderDetailRow label="To">{to}</HeaderDetailRow> : null}
          {cc ? <HeaderDetailRow label="Cc">{cc}</HeaderDetailRow> : null}
          {bcc ? <HeaderDetailRow label="Bcc">{bcc}</HeaderDetailRow> : null}
          <HeaderDetailRow label="Date">
            {dayjs(message.sentAt ?? message.createdAt).format(
              "MMM D, YYYY, h:mm A",
            )}
          </HeaderDetailRow>
          <HeaderDetailRow label="Subject">
            {message.subject || "(No subject)"}
          </HeaderDetailRow>
          {message.mailingListName || message.mailingListAddress ? (
            <HeaderDetailRow label="Mailing list">
              {message.mailingListName ? `“${message.mailingListName}”` : ""}
              {message.mailingListName && message.mailingListAddress ? " " : ""}
              {message.mailingListAddress
                ? `<${message.mailingListAddress}>`
                : ""}
            </HeaderDetailRow>
          ) : null}
          {message.mailedBy ? (
            <HeaderDetailRow label="Mailed by">
              {message.mailedBy}
            </HeaderDetailRow>
          ) : null}
          {message.signedBy ? (
            <HeaderDetailRow label="Signed by">
              {message.signedBy}
            </HeaderDetailRow>
          ) : null}
          {message.securityInfo ? (
            <HeaderDetailRow label="Security">
              <span className="inline-flex items-center gap-1.5">
                <LockSimple size={13} weight="fill" />
                {message.securityInfo}
              </span>
            </HeaderDetailRow>
          ) : null}
        </dl>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ThreadView({
  detail,
  loading,
  onBack,
  folder,
  onAction,
  onPermanentDelete,
  labels,
  onLabels,
  compose,
  mailboxAddress,
  darkMode,
  conversationPosition,
  conversationTotal,
  onPreviousConversation,
  onNextConversation,
}: {
  detail: ThreadDetail | undefined;
  loading: boolean;
  onBack: () => void;
  onAction: (action: string) => void;
  labels: LabelDto[];
  folder: SystemFolder;
  onPermanentDelete: () => void;
  onLabels: (ids: string[]) => void;
  compose: ReturnType<typeof useMailShell>["compose"];
  mailboxAddress: string;
  darkMode: boolean;
  conversationPosition: number | null;
  conversationTotal: number;
  onPreviousConversation: (() => void) | undefined;
  onNextConversation: (() => void) | undefined;
}) {
  if (loading) return <ThreadSkeleton full />;
  if (!detail)
    return (
      <EmptyState
        title="Unable to load message"
        body="The message may have been deleted."
      />
    );
  const last = detail.messages.at(-1)!;
  const replyAddress = last.replyTo ?? last.fromAddress;
  const replyAll = [
    ...new Set(
      [
        replyAddress,
        ...last.recipients
          .filter((item) => item.type !== "BCC")
          .map((item) => item.address),
      ].filter(
        (address) => address.toLowerCase() !== mailboxAddress.toLowerCase(),
      ),
    ),
  ];
  const detailLabelIds = new Set(
    detail.placement.labels.map((label) => label.id),
  );
  return (
    <div className="min-h-full">
      <div className="mail-detail-toolbar sticky top-0 z-10 flex h-13 items-center gap-1 border-b px-3">
        <ToolbarAction label="Back" onClick={onBack}>
          <MaterialIcon>arrow_back</MaterialIcon>
        </ToolbarAction>
        <div className="mail-detail-back-gap" aria-hidden="true" />
        {folder !== "DRAFT" ? (
          folder === "TRASH" ? (
            <ToolbarAction label="Restore" onClick={() => onAction("RESTORE")}>
              <MaterialIcon>restore_from_trash</MaterialIcon>
            </ToolbarAction>
          ) : folder === "ARCHIVE" || folder === "SPAM" ? (
            <ToolbarAction
              label="Move to Inbox"
              onClick={() => onAction("MOVE_INBOX")}
            >
              <MaterialIcon>move_to_inbox</MaterialIcon>
            </ToolbarAction>
          ) : (
            <ToolbarAction label="Archive" onClick={() => onAction("ARCHIVE")}>
              <MaterialIcon>archive</MaterialIcon>
            </ToolbarAction>
          )
        ) : null}
        {folder !== "DRAFT" && folder !== "TRASH" && folder !== "SPAM" ? (
          <ToolbarAction
            label="Report spam"
            onClick={() => onAction("MOVE_SPAM")}
          >
            <MaterialIcon>report</MaterialIcon>
          </ToolbarAction>
        ) : null}
        <ToolbarAction
          label={folder === "TRASH" ? "Delete permanently" : "Delete"}
          onClick={
            folder === "TRASH"
              ? onPermanentDelete
              : () => onAction("MOVE_TRASH")
          }
        >
          <MaterialIcon>delete</MaterialIcon>
        </ToolbarAction>
        <div className="mx-1 h-5 w-px bg-border" />
        {folder !== "DRAFT" ? (
          <ToolbarAction
            label="Mark as unread"
            onClick={() => onAction("MARK_UNREAD")}
          >
            <MaterialIcon>mail</MaterialIcon>
          </ToolbarAction>
        ) : null}
        <ToolbarAction label="Snooze" onClick={ignoreUnavailableAction}>
          <MaterialIcon>schedule</MaterialIcon>
        </ToolbarAction>
        <ToolbarAction label="Add to Tasks" onClick={ignoreUnavailableAction}>
          <MaterialIcon>add_task</MaterialIcon>
        </ToolbarAction>
        <div className="mx-1 h-5 w-px bg-border" />
        <MoveMenu folder={folder} onMove={onAction} />
        <LabelPicker
          labels={labels}
          activeIds={detailLabelIds}
          onToggle={(labelId) => {
            const next = new Set(detailLabelIds);
            if (next.has(labelId)) next.delete(labelId);
            else next.add(labelId);
            onLabels([...next]);
          }}
        />
        <ToolbarAction label="More" onClick={ignoreUnavailableAction}>
          <MaterialIcon>more_vert</MaterialIcon>
        </ToolbarAction>
        <div
          className="mail-detail-navigation"
          aria-label="Conversation navigation"
        >
          <span aria-live="polite">
            {conversationPosition
              ? `${conversationPosition} of ${conversationTotal}`
              : `${conversationTotal} conversations`}
          </span>
          <button
            type="button"
            aria-label="Previous conversation"
            disabled={!onPreviousConversation}
            onClick={onPreviousConversation}
          >
            <MaterialIcon>chevron_left</MaterialIcon>
          </button>
          <button
            type="button"
            aria-label="Next conversation"
            disabled={!onNextConversation}
            onClick={onNextConversation}
          >
            <MaterialIcon>chevron_right</MaterialIcon>
          </button>
          <InputToolsButton />
        </div>
      </div>
      <div className="thread-detail-content mx-auto px-5 py-6 md:px-8">
        <div className="mail-detail-subject-row mb-6 flex items-start gap-2">
          <h1 className="mail-detail-subject min-w-0 text-[24px] font-[650] leading-tight tracking-[-.035em]">
            {detail.subject}
          </h1>
          <div className="mail-detail-labels">
            {detail.placement.labels.map((label) => (
              <span key={label.id} style={{ background: label.color }}>
                {displayMailLabelName(label.name)}
              </span>
            ))}
          </div>
          <div className="ml-auto flex shrink-0">
            <ToolbarAction label="Print all" onClick={ignoreUnavailableAction}>
              <MaterialIcon>print</MaterialIcon>
            </ToolbarAction>
            <ToolbarAction
              label="Open in new window"
              onClick={ignoreUnavailableAction}
            >
              <MaterialIcon>open_in_new</MaterialIcon>
            </ToolbarAction>
          </div>
        </div>
        <div className="space-y-4">
          {detail.messages.map((message) => (
            <article
              key={message.id}
              className="mail-message-card rounded-lg border"
            >
              <header className="mail-message-header flex gap-3 px-4 py-3.5">
                {message.fromAvatarUrl ? (
                  <img
                    src={absoluteAssetUrl(message.fromAvatarUrl)}
                    alt=""
                    className="identity-avatar size-10 rounded-full object-cover"
                  />
                ) : (
                  <span
                    className="identity-avatar grid size-10 shrink-0 place-items-center rounded-full text-xs font-bold text-white"
                    style={{ background: avatarColor(message.fromAddress) }}
                  >
                    {initials(message.fromName, message.fromAddress)}
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="font-[640]">
                      {message.fromName || message.fromAddress}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      &lt;{message.fromAddress}&gt;
                    </span>
                    {message.unsubscribeUrl ? (
                      <a
                        href={message.unsubscribeUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs font-[590] text-primary hover:underline"
                      >
                        Unsubscribe
                      </a>
                    ) : null}
                  </div>
                  <div className="mt-0.5 flex min-w-0 items-center gap-0.5 text-xs text-muted-foreground">
                    <span className="truncate">
                      to{" "}
                      {message.recipients
                        .filter((item) => item.type !== "BCC")
                        .map((item) => item.displayName || item.address)
                        .join(", ")}
                    </span>
                    <MessageHeaderDetails message={message} />
                  </div>
                </div>
                <div className="mail-message-header-actions flex shrink-0 items-start gap-0.5">
                  <time className="mr-1 pt-2 text-[11px] text-muted-foreground">
                    {dayjs(message.sentAt ?? message.createdAt).format(
                      "MMM D, h:mm A",
                    )}
                  </time>
                  <ToolbarAction
                    label={
                      detail.placement.isStarred ? "Remove star" : "Add star"
                    }
                    onClick={() =>
                      onAction(detail.placement.isStarred ? "UNSTAR" : "STAR")
                    }
                  >
                    <MaterialIcon
                      className={
                        detail.placement.isStarred
                          ? "filled text-[#f4b401]"
                          : ""
                      }
                    >
                      star
                    </MaterialIcon>
                  </ToolbarAction>
                  <ToolbarAction
                    label="Add emoji reaction"
                    onClick={ignoreUnavailableAction}
                  >
                    <MaterialIcon>add_reaction</MaterialIcon>
                  </ToolbarAction>
                  <ToolbarAction
                    label="Reply"
                    onClick={() =>
                      compose({
                        title: "Reply",
                        to: [message.replyTo ?? message.fromAddress],
                        subject: detail.subject.startsWith("Re:")
                          ? detail.subject
                          : `Re: ${detail.subject}`,
                        threadId: detail.id,
                        inReplyToMessageId: message.id,
                      })
                    }
                  >
                    <MaterialIcon>reply</MaterialIcon>
                  </ToolbarAction>
                  <ToolbarAction label="More" onClick={ignoreUnavailableAction}>
                    <MaterialIcon>more_vert</MaterialIcon>
                  </ToolbarAction>
                </div>
              </header>
              <div className="mail-message-body border-t px-4 py-3">
                <MessageBodyFrame
                  messageId={message.id}
                  bodyHtml={message.bodyHtml}
                  darkMode={darkMode}
                />
              </div>
              {message.attachments.length ? (
                <div className="mail-message-attachments flex flex-wrap gap-2 border-t px-4 py-3">
                  {message.attachments.map((asset) => (
                    <a
                      key={asset.id}
                      href={absoluteAssetUrl(asset.url)}
                      className="mail-attachment inline-flex items-center gap-2 rounded-md border px-3 py-2 text-xs"
                    >
                      <Paperclip size={15} />
                      <span>{asset.filename}</span>
                      <span className="text-muted-foreground">
                        {formatSize(asset.size)}
                      </span>
                    </a>
                  ))}
                </div>
              ) : null}
            </article>
          ))}
        </div>
        {folder !== "DRAFT" ? (
          <div className="mail-reply-actions mt-5 flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() =>
                compose({
                  title: "Reply",
                  to: [replyAddress],
                  subject: detail.subject.startsWith("Re:")
                    ? detail.subject
                    : `Re: ${detail.subject}`,
                  threadId: detail.id,
                  inReplyToMessageId: last.id,
                })
              }
            >
              <MaterialIcon>reply</MaterialIcon>Reply
            </Button>
            {replyAll.length > 1 ? (
              <Button
                variant="outline"
                onClick={() =>
                  compose({
                    title: "Reply all",
                    to: replyAll,
                    subject: detail.subject.startsWith("Re:")
                      ? detail.subject
                      : `Re: ${detail.subject}`,
                    threadId: detail.id,
                    inReplyToMessageId: last.id,
                  })
                }
              >
                <MaterialIcon>reply_all</MaterialIcon>Reply all
              </Button>
            ) : null}
            <Button
              variant="outline"
              onClick={() =>
                compose({
                  title: "Forward",
                  subject: detail.subject.startsWith("Fwd:")
                    ? detail.subject
                    : `Fwd: ${detail.subject}`,
                  bodyHtml: `<p></p><blockquote>${last.bodyHtml}</blockquote>`,
                  forwardedFromMessageId: last.id,
                })
              }
            >
              <MaterialIcon>forward</MaterialIcon>Forward
            </Button>
            <Button
              variant="outline"
              className="gmail-reaction-button"
              aria-label="Add emoji reaction"
              onClick={ignoreUnavailableAction}
            >
              <MaterialIcon>add_reaction</MaterialIcon>
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="grid min-h-[360px] place-items-center px-5 text-center">
      <div>
        <EnvelopeOpen className="mx-auto text-muted-foreground/55" size={38} />
        <h2 className="mt-4 text-base font-[630]">{title}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{body}</p>
      </div>
    </div>
  );
}

function ThreadSkeleton({
  categories = false,
  full = false,
}: {
  categories?: boolean;
  full?: boolean;
}) {
  return (
    <div
      className={cn(
        "thread-skeleton",
        !categories && "without-categories",
        full && "full",
      )}
      role="status"
      aria-label="Loading mail"
    >
      {Array.from({ length: 28 }).map((_, index) => (
        <div key={index} className="thread-skeleton-row">
          <span className="thread-skeleton-check" />
          <span className="thread-skeleton-star" />
          <span className="thread-skeleton-sender" />
          <span className="thread-skeleton-content" />
          <span className="thread-skeleton-time" />
        </div>
      ))}
    </div>
  );
}

function formatThreadDate(value: string) {
  const date = dayjs(value);
  return date.isSame(dayjs(), "day")
    ? date.format("h:mm A")
    : date.isSame(dayjs(), "year")
      ? date.format("MMM D")
      : date.format("MMM D, YYYY");
}

function formatSize(size: number) {
  return size < 1024
    ? `${size} B`
    : size < 1024 * 1024
      ? `${(size / 1024).toFixed(1)} KB`
      : `${(size / 1024 / 1024).toFixed(1)} MB`;
}
